<template>
  <v-text-field ref="numberField" type="number" class="brand-number-field" v-bind="mergedAttrs">
    <template v-for="(_, slotName) in $slots" :key="slotName" #[slotName]="slotData">
      <slot :name="slotName" v-bind="slotData || {}" />
    </template>
  </v-text-field>
</template>

<script setup>
import { computed, useAttrs, ref } from 'vue'
import { mergeAttrs } from '@/utils/vue-attrs'

const props = defineProps({
  variant: {
    type: String,
    default: null,
  },
})

const attrs = useAttrs()
const numberField = ref(null)

const VARIANTS = {
  basic: {
    variant: 'outlined',
    bg: 'neutral0',
    color: 'blue400',
    'base-color': 'neutral50',
    classes: ['brand-number-field--basic', 'rounded-lg'],
  },
}

const commonAttrs = computed(() => {
  const variant = VARIANTS[props.variant]
  const common = {
    'hide-details': 'auto',
    'validate-on': 'lazy',
  }

  return {
    ...common,
    ...variant,
    class: [
      ...(variant?.classes ?? []),
    ],
  }
})

const mergedAttrs = computed(() => {
  return mergeAttrs(commonAttrs.value, attrs)
})

const focus = () => {
  numberField.value?.$el.querySelector('input')?.focus()
}

const validate = () => {
  numberField.value?.validate()
}

defineExpose({
  focus,
  validate,
})

defineOptions({
  inheritAttrs: false,
})
</script>

<script>
export default {
  inheritAttrs: false,
}
</script>

<style lang="scss" scoped>
.v-text-field.brand-number-field {
  &.brand-number-field--basic:not(.v-input--disabled) {
    :deep(.v-field__outline) {
      color: rgba(0, 0, 0, 0.15);
    }

    :deep(.v-field) {
      border-radius: 12px !important;
    }

    :deep(.v-field__input) {
      min-height: 48px;
      padding-top: 14px;
      padding-bottom: 14px;
    }
  }

  // Hide number input spinner buttons
  :deep(input[type='number']) {
    -moz-appearance: textfield;
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }
}
</style>

<style lang="scss">
.v-theme--dark {
  .v-text-field.brand-number-field.brand-number-field--basic:not(.v-input--disabled) {
    .v-field__outline {
      color: rgb(var(--v-theme-neutral50)) !important;
    }
  }
}
</style>
